import { WithAuthenticationRequiredOptions } from '@auth0/auth0-react'
import React, { FC, useEffect } from 'react'

import useSiteMetadata from '../hooks/useSiteMetadata'
import { useAuth } from '../lib/auth'
import { useDispensaryContext } from '../lib/dispensaries'

const defaultOnRedirecting = (): JSX.Element => <></>

const defaultReturnTo = (): string =>
  typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : ''

export type WithAuthenticationOrDispensaryRequiredOptions = WithAuthenticationRequiredOptions

/**
 * Higher order component extending the logic of Auth0's withAuthenticationRequired() HOC to
 * allow guest users when either the feature is enabled for all contexts or when in dispensary context
 */
export const withAuthenticationRequiredOrAnonymousCart = <TProps extends Record<string, unknown>>(
  ComponentToWrap: React.ComponentType<TProps>,
  options: WithAuthenticationOrDispensaryRequiredOptions = {},
): FC<TProps> => {
  return function WithAuthenticationRequiredOrAnonymousCartAllowed(props: TProps): JSX.Element {
    const { featureFlags } = useSiteMetadata() ?? {}
    const { user, isAuthenticated, isLoading, loginWithRedirect } = useAuth()
    const dispensary = useDispensaryContext()
    const {
      returnTo = defaultReturnTo,
      onRedirecting = defaultOnRedirecting,
      loginOptions = {},
      claimCheck = (): boolean => true,
    } = options

    /**
     * The route is authenticated if the user has valid auth and there are no
     * JWT claim mismatches.
     */
    const routeIsAuthenticated = isAuthenticated && claimCheck(user)

    const routeIsDispensary = !!dispensary

    const anonymousCartEnabled = featureFlags?.anonymousCart ?? false

    const routeIsAllowed = routeIsAuthenticated || routeIsDispensary || anonymousCartEnabled

    useEffect(() => {
      if (isLoading || routeIsAllowed) {
        return
      }
      const opts = {
        ...loginOptions,
        appState: {
          ...loginOptions.appState,
          returnTo: typeof returnTo === 'function' ? returnTo() : returnTo,
        },
      }
      ;(async (): Promise<void> => {
        await loginWithRedirect(opts)
      })()
    }, [isLoading, routeIsAllowed, loginWithRedirect, loginOptions, returnTo])

    return routeIsAllowed ? <ComponentToWrap {...props} /> : onRedirecting()
  }
}
